// TestRunnerScreen.tsx
// Automated test runner with visual status display
// Triggered by URL (?autoTest=true) or manually from debug panel

import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Pressable, ActivityIndicator } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import {
  PageContainer,
  Text,
  Button,
  Card,
  Theme,
} from '../../components/ui';
import { TestRunnerService, TestExecution, TestStatus } from '../../services/testRunner';
import { TestCleanup } from '../../utils/testCleanup';

const TestRunnerScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const [executions, setExecutions] = useState<TestExecution[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [summary, setSummary] = useState<{ passed: number; failed: number; skipped: number } | null>(null);
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  // Check if auto-test is enabled via URL parameter
  useEffect(() => {
    const params = route.params as any;
    if (params?.autoTest === 'true' || params?.autoTest === true) {
      console.log('🤖 Auto-test triggered via URL parameter');
      handleRunTests();
    }
  }, [route.params]);

  const handleRunTests = async () => {
    setIsRunning(true);
    setExecutions([]);
    setSummary(null);

    console.log('🚀 Starting automated test run...');

    const result = await TestRunnerService.runAllTests((execution) => {
      // Update UI as each test progresses
      setExecutions(prev => {
        const existing = prev.find(e => e.test.id === execution.test.id);
        if (existing) {
          // Update existing
          return prev.map(e => e.test.id === execution.test.id ? execution : e);
        } else {
          // Add new
          return [...prev, execution];
        }
      });
    });

    setSummary({
      passed: result.passed,
      failed: result.failed,
      skipped: result.skipped,
    });
    setIsRunning(false);

    console.log('🏁 Test run completed');
  };

  const handleCleanup = async () => {
    setIsCleaningUp(true);
    console.log('🧹 Starting cleanup...');
    
    const result = await TestCleanup.cleanupAfterTests();
    
    if (result.success) {
      console.log(`✅ Cleanup complete - Profile deleted: ${result.profileDeleted}, Signed out: ${result.signedOut}`);
      alert(`Cleanup complete!\nProfile deleted: ${result.profileDeleted}\nSigned out: ${result.signedOut}`);
    } else {
      console.error(`❌ Cleanup failed: ${result.error}`);
      alert(`Cleanup failed: ${result.error}`);
    }
    
    setIsCleaningUp(false);
  };

  const getStatusIcon = (status: TestStatus): string => {
    switch (status) {
      case 'pending': return '⏳';
      case 'running': return '🔄';
      case 'passed': return '✅';
      case 'failed': return '❌';
      case 'skipped': return '⏭️';
      default: return '❓';
    }
  };

  const getStatusColor = (status: TestStatus): string => {
    switch (status) {
      case 'passed': return Theme.colors.success;
      case 'failed': return Theme.colors.error;
      case 'running': return Theme.colors.primary;
      default: return Theme.colors.textSecondary;
    }
  };

  return (
    <PageContainer>
      {/* Compact close button */}
      <Pressable 
        style={styles.closeButton}
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="close" size={24} color={Theme.colors.textPrimary} />
      </Pressable>

      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        {/* Header */}
        <View style={styles.header}>
          <Text variant="heading1" style={styles.title}>
            Automated Test Runner
          </Text>
          <Text variant="body2" color="secondary" align="center">
            URL trigger: ?autoTest=true
          </Text>
        </View>

        {/* Controls */}
        <View style={styles.controls}>
          <Button
            title={isRunning ? 'Running Tests...' : 'Run All Tests'}
            onPress={handleRunTests}
            disabled={isRunning}
            style={styles.runButton}
          />
          
          {summary && (
            <Button
              title={isCleaningUp ? 'Cleaning Up...' : 'Cleanup Test Data'}
              onPress={handleCleanup}
              disabled={isCleaningUp || isRunning}
              variant="secondary"
              style={styles.cleanupButton}
            />
          )}
        </View>

        {/* Summary */}
        {summary && (
          <Card style={styles.summaryCard}>
            <Text variant="heading3" style={styles.summaryTitle}>
              Test Summary
            </Text>
            <View style={styles.summaryRow}>
              <Text variant="body1">✅ Passed:</Text>
              <Text variant="body1" style={{ color: Theme.colors.success, fontWeight: 'bold' }}>
                {summary.passed}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text variant="body1">❌ Failed:</Text>
              <Text variant="body1" style={{ color: Theme.colors.error, fontWeight: 'bold' }}>
                {summary.failed}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text variant="body1">⏭️ Skipped:</Text>
              <Text variant="body1" style={{ color: Theme.colors.textSecondary }}>
                {summary.skipped}
              </Text>
            </View>
          </Card>
        )}

        {/* Test Results */}
        {executions.length > 0 && (
          <Card style={styles.resultsCard}>
            <Text variant="heading3" style={styles.sectionTitle}>
              Test Results
            </Text>
            
            {executions.map((execution, index) => (
              <View key={execution.test.id} style={styles.testRow}>
                <View style={styles.testInfo}>
                  <Text variant="body1" style={{ fontSize: 20, marginRight: 8 }}>
                    {getStatusIcon(execution.status)}
                  </Text>
                  <View style={{ flex: 1 }}>
                    <Text 
                      variant="body1" 
                      style={{ color: getStatusColor(execution.status) }}
                    >
                      {execution.test.name}
                    </Text>
                    <Text variant="caption" color="secondary">
                      {execution.test.category} • {execution.test.id}
                    </Text>
                    {execution.result && (
                      <Text 
                        variant="caption" 
                        style={{ 
                          marginTop: 4,
                          color: execution.result.success ? Theme.colors.success : Theme.colors.error 
                        }}
                      >
                        {execution.result.message || (execution.result.success ? 'Passed' : 'Failed')}
                        {' • '}
                        {execution.result.duration}ms
                      </Text>
                    )}
                  </View>
                </View>
                {execution.status === 'running' && (
                  <ActivityIndicator size="small" color={Theme.colors.primary} />
                )}
              </View>
            ))}
          </Card>
        )}

        {executions.length === 0 && !isRunning && (
          <Card style={styles.emptyCard}>
            <Text variant="body1" align="center" color="secondary">
              No tests run yet. Click "Run All Tests" to start.
            </Text>
          </Card>
        )}
      </ScrollView>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 16,
    zIndex: 1000,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Theme.colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: Theme.spacing.xl,
  },
  header: {
    marginBottom: Theme.spacing.xl,
    alignItems: 'center',
  },
  title: {
    marginBottom: Theme.spacing.sm,
  },
  controls: {
    marginBottom: Theme.spacing.xl,
    gap: Theme.spacing.md,
  },
  runButton: {
    width: '100%',
  },
  cleanupButton: {
    width: '100%',
  },
  summaryCard: {
    marginBottom: Theme.spacing.lg,
    padding: Theme.spacing.lg,
  },
  summaryTitle: {
    marginBottom: Theme.spacing.md,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: Theme.spacing.sm,
  },
  resultsCard: {
    padding: Theme.spacing.lg,
  },
  sectionTitle: {
    marginBottom: Theme.spacing.md,
  },
  testRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: Theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Theme.colors.border,
  },
  testInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  emptyCard: {
    padding: Theme.spacing.xxxl,
  },
});

export default TestRunnerScreen;

